import * as React from 'react';
import {useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid2';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import Info from './components/Info';
import InfoMobile from './components/InfoMobile';
import PaymentForm from './components/PaymentForm';
import Review from './components/Review';
import CartManager, {getDiscountedPrice, useCartChange} from "../managers/cartManager/cartManager";
import {Trans, useTranslation} from "react-i18next";
import DesignsPage from "./designsPage/DesignsPage";
import Shipping from "./components/shipping/Shipping";
import EndOfOrder from "./components/EndOfOrder";
import {useNavigate, useSearchParams} from "react-router";
import {CircularProgress} from "@mui/material";
import Alert from "@mui/material/Alert";
import Container from "@mui/material/Container";
import UserInfo from "./components/billingData/UserInfo";

const steps = ['designs', 'data', 'shipping', 'payment', 'review'];

export default function Checkout() {
    const {i18n} = useTranslation();
    const [params] = useSearchParams();
    const verification = params.get('netopia-verification');

    const cart = useCartChange();
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(CartManager.getActiveStep());
    const [errorMessageCode, setErrorMessageCode] = useState('');
    const [acceptedTermsAndConditions, setAcceptedTermsAndConditions] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (CartManager.getTotalNumberOfItems() === 0 || !CartManager.validateSelectedDesigns()) {
            setActiveStep(0);
        } else {
            setActiveStep(CartManager.getActiveStep());
        }
    }, [cart])


    function getStepContent(step) {
        switch (step) {
            case 0:
                return <DesignsPage/>;
            case 1:
                return <UserInfo/>;
            case 2:
                return <Shipping/>;
            case 3:
                return <PaymentForm/>;
            case 4:
                return <Review accepted={acceptedTermsAndConditions} setAccepted={setAcceptedTermsAndConditions}/>;
            default:
                throw new Error('Unknown step');
        }
    }

    const handleNext = () => {
        if (activeStep + 1 === steps.length) {
            setLoading(true);
            CartManager.sendOrder(navigate, i18n.language, openPopup);
        } else {
            CartManager.setActiveStep(activeStep + 1);
        }
    };

    const openPopup = (message) => {
        setErrorMessageCode(message);
        setTimeout(() => {
            setErrorMessageCode('');
        }, 10000)
    };

    const handleBack = () => {
        setActiveStep(activeStep - 1);
        CartManager.setActiveStep(activeStep - 1);
    };

    const goBackToFirstStep = () => {
        setActiveStep(0);
        CartManager.setActiveStep(0);
    }

    const stepIsNotValid = () => {
        return !validateStep(activeStep);
    };

    const validateStep = (step) => {
        if (CartManager.hasDisabledItems()) {
            return false;
        }

        if (step === 0) {
            return !(CartManager.getTotalNumberOfItems() === 0) && CartManager.validateSelectedDesigns() && CartManager.validateFileParams();
        }

        if (step === 1) {
            return CartManager.validateInvoiceData();
        }

        if (step === 2) {
            return CartManager.validateShipment();
        }

        if (step === 3) {
            return CartManager.validatePayment();
        }

        if (step === 4) {
            return acceptedTermsAndConditions;
        }

        return true;
    };

    const goToStep = (value) => {
        if (value < activeStep) {
            setActiveStep(value);
            CartManager.setActiveStep(value);
        } else {
            let canGoTo = activeStep;
            while ((canGoTo < value) && (validateStep(canGoTo))) {
                canGoTo++;
            }
            setActiveStep(canGoTo);
            CartManager.setActiveStep(canGoTo);
        }
    }

    return (
        <div>
            {loading ? <Box sx={{
                position: 'fixed',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 100,
                backgroundColor: 'rgba(0, 0, 0, 0.35)'
            }}><CircularProgress/></Box> : ''}
            {errorMessageCode ?
                <Container maxWidth="md" onClick={() => setErrorMessageCode('')} sx={{cursor: 'pointer'}}>
                    <Alert severity="error"><Trans i18nKey={errorMessageCode}/></Alert>
                </Container> : ''}
            <Grid
                container
                sx={{
                    height: {
                        xs: '100%',
                        sm: '100%',
                    },
                    mt: {
                        xs: 4,
                        sm: 0,
                    },
                }}
            >
                <Grid
                    size={{sm: 12, md: 7, lg: 7}}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        maxWidth: '100%',
                        width: '100%',
                        backgroundColor: {xs: 'transparent', sm: 'background.default'},
                        alignItems: 'end',
                        pt: {xs: 0, sm: 4},
                        px: {xs: 2, sm: 10},
                        gap: {xs: 3, md: 3},
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: {sm: 'space-between', md: 'flex-end'},
                            alignItems: 'center',
                            width: '100%',
                            maxWidth: {sm: '100%', md: 720},
                        }}
                    >
                        <Box
                            sx={{
                                display: {xs: 'none', md: 'flex'},
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                alignItems: 'flex-end',
                                flexGrow: 1,
                            }}
                        >
                            <Stepper
                                id="desktop-stepper"
                                activeStep={activeStep}
                                sx={{width: '100%', height: 40}}
                            >
                                {steps.map((label, index) => (
                                    <Step
                                        sx={{
                                            ':first-of-type': {pl: 0},
                                            ':last-child': {pr: 0},
                                            cursor: 'pointer !important',
                                            '& *': {cursor: 'pointer !important'}
                                        }}
                                        key={label}
                                        onClick={() => goToStep(index)}
                                    >
                                        <StepLabel><Trans i18nKey={`cart.${label}`}/></StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </Box>
                    </Box>
                    <Card sx={{display: {xs: 'flex', md: 'none'}, width: '100%'}}>
                        <CardContent
                            sx={{
                                display: 'flex',
                                width: '100%',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <div>
                                <Typography variant="subtitle2" gutterBottom>
                                    <Trans i18nKey={'cart.selectedProducts'}/>
                                </Typography>
                                <Typography variant="body1">
                                    {(getDiscountedPrice(cart) + CartManager.getShippingPrice()).toFixed(2)} Lei
                                </Typography>
                            </div>
                            <InfoMobile goBackToFirstStep={goBackToFirstStep}/>
                        </CardContent>
                    </Card>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            width: '100%',
                            maxWidth: {sm: '100%', md: 720},
                            gap: {xs: 5, md: 'none'},
                        }}
                    >
                        <Stepper
                            id="mobile-stepper"
                            activeStep={activeStep}
                            alternativeLabel
                            sx={{display: {sm: 'flex', md: 'none'}}}
                        >
                            {steps.map((label, index) => (
                                <Step
                                    sx={{
                                        ':first-of-type': {pl: 0},
                                        ':last-child': {pr: 0},
                                    }}
                                    key={label}
                                    onClick={() => goToStep(index)}
                                >
                                    <StepLabel
                                        sx={{'.MuiStepLabel-labelContainer': {maxWidth: '70px'}}}
                                    >
                                        <Trans i18nKey={`cart.${label}`}/>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                        {activeStep === steps.length ? (
                            <EndOfOrder/>
                        ) : (
                            <React.Fragment>
                                {getStepContent(activeStep)}
                                <Box
                                    sx={[
                                        {
                                            display: 'flex',
                                            flexDirection: {xs: 'column-reverse', sm: 'row'},
                                            alignItems: 'end',
                                            flexGrow: 1,
                                            gap: 1,
                                            pb: {xs: 4, sm: 0},
                                            mt: {xs: 2, sm: 0},
                                        },
                                        activeStep !== 0
                                            ? {justifyContent: 'space-between'}
                                            : {justifyContent: 'flex-end'},
                                    ]}
                                >
                                    {activeStep !== 0 && (
                                        <Button
                                            startIcon={<ChevronLeftRoundedIcon/>}
                                            onClick={handleBack}
                                            variant="text"
                                            sx={{display: {xs: 'none', sm: 'flex'}}}
                                        >
                                            <Trans i18nKey="cart.previous"/>
                                        </Button>
                                    )}
                                    {activeStep !== 0 && (
                                        <Button
                                            startIcon={<ChevronLeftRoundedIcon/>}
                                            onClick={handleBack}
                                            variant="outlined"
                                            fullWidth
                                            sx={{display: {xs: 'flex', sm: 'none'}}}
                                        >
                                            <Trans i18nKey="cart.previous"/>
                                        </Button>
                                    )}
                                    <Button
                                        disabled={stepIsNotValid()}
                                        variant="contained"
                                        endIcon={<ChevronRightRoundedIcon/>}
                                        onClick={handleNext}
                                        sx={{width: {xs: '100%', sm: 'fit-content'}}}
                                    >
                                        <Trans
                                            i18nKey={(activeStep === steps.length - 1) ? 'cart.placeOrder' : 'cart.next'}/>
                                    </Button>
                                </Box>
                            </React.Fragment>
                        )}
                    </Box>
                </Grid>
                <Grid
                    size={{xs: 12, sm: 5, lg: 5}}
                    sx={{
                        display: {xs: 'none', md: 'flex'},
                        flexDirection: 'column',
                        backgroundColor: 'background.paper',
                        borderRight: {sm: 'none', md: '1px solid'},
                        borderColor: {sm: 'none', md: 'divider'},
                        alignItems: 'start',
                        pt: 16,
                        px: 10,
                        gap: 4,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            width: '100%',
                            maxWidth: 500,
                        }}
                    >
                        <Info goBackToFirstStep={goBackToFirstStep}/>
                    </Box>
                </Grid>
            </Grid>
        </div>
    );
}
